import { useState, useEffect, useCallback, useRef } from "react";
import DynamicTable from "../../ui/DynamicTable";
import config from "../../../config/config";
import { BsThreeDotsVertical } from "react-icons/bs";
import { userSession } from "../../../observers/userObserver";
import Pagination from "../../ui/Pagination";
import { refreshCountryTableData } from "../../../observers/dataObserver";
import { codService } from "../../../requests/codAdminService";


const CountryTable = () => {
    const [rows, setRows] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState({});
    const [pagination, setPagination] = useState({
        current_page: 1,
        total_items: 0,
        total_pages: 0,
        limit: 10
    });
    const dropdownRef = useRef(null);

    const token = userSession.getToken();
    
    const getAuthHeaders = () => ({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
    });

    const handlePageChange = (pageNumber) => {
        setPagination((prevPagination) => ({
            ...prevPagination,
            current_page: pageNumber
        }));
    };

    const toggleDropdown = useCallback((id) => {
        setDropdownOpen((prev) => {
            const isOpen = prev[id];
            return isOpen
            ? {} // Close all if current is open
            : { [id]: true }; // Open only the current one
        });
    },[]);

    const fetchCountries = useCallback(async () => {
        try {
          const { countries, pagination: newPagination } = await codService.getCountriesWithPagination(
            pagination.current_page,
            pagination.limit
          );
      
          setRows(countries);
          setPagination(newPagination);
        } catch (error) {
          console.error("❌ Error fetching countries:", error);
        }
      }, [pagination.current_page, pagination.limit]);
      
      const toggleRowStatus = useCallback(
        async (row, status) => {
          try {
            await codService.updated_row(row, status);
      
            // Reset to page 1 after status change
            setPagination((prev) => ({
              ...prev,
              current_page: 1
            }));
      
            fetchCountries();
          } catch (error) {
            console.error("❌ Failed to update row status:", error);
          }
        },
        [fetchCountries]
      );

    const renderActionDropdown = useCallback((_, row) => {
        return (
            <div className="dropdown-container" key={row.id} ref={dropdownOpen[row.id] ? dropdownRef : null}>
            <button
                onClick={() => toggleDropdown(row.id)}
                className="dropdown-btn"
            >
                <BsThreeDotsVertical size={20} />
            </button>
            {dropdownOpen[row.id] && (
                <div className="custom-dropdown">
                    {!row.status ? <div
                        className="dropdownItem"
                        onClick={() => {toggleRowStatus(row, "Activate"); toggleDropdown(row.id)}}
                    >
                        Activate
                    </div> : <div
                        className="dropdownItem"
                        onClick={() => {toggleRowStatus(row, "Deactivate"); toggleDropdown(row.id)}}
                    >
                        Deactivate
                    </div>}
                </div>
            )}
            </div>
        );
    },[dropdownOpen, toggleDropdown, toggleRowStatus, dropdownRef]);

    const renderStatus = useCallback((value) => (
        <span className={`status-tile status-${value ? "active" : "inactive"}`}>
          {value ? "Active" : "Inactive"}
        </span>
    ),[]);

    useEffect(() => {
        fetchCountries();

        refreshCountryTableData.subscribe(() => {
            fetchCountries();
        });

    }, [fetchCountries]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen({});
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const columns = [
                    { key: "country", label: "Country" },
                    { key: "diligence", label: "Diligence" },
                    { key: "status", label: "Status", render: renderStatus },
                    { key: "active", label: "Actions", render: renderActionDropdown },
                ]

    return (
        <>
            <DynamicTable columns={columns} data={rows} />
            <Pagination totalPages={pagination.total_pages} page={pagination.current_page} handlePageChange={handlePageChange} />
        </>
    )

}

export default CountryTable;